/* React */
import React from "react";

/* React Redux */
import { useSelector } from "react-redux";
import { RootState } from "src/redux/store";

/* Ionic */
import { IonGrid, IonRow, IonCol } from "@ionic/react";

/* Components */
import ColorLabel from "src/components/charts/chart-legend/color-label/ColorLabel";

/* Utils */
import { getUnitFromFeatureOrCohort } from "src/utils/helper/charts/helper";

/* Constants */
import { Insights } from "src/shared/constants/common/Common";
import { HAS_VALUE_LABEL } from "src/shared/constants/charts/NoValueStates";
import { ChartType } from "src/shared/constants/charts/Chart";

/* Stylesheet */
import styles from "./Cohorts.module.scss";

/* Interfaces */
import { LegendProps } from "src/shared/interfaces/charts/Legend";

// Survival analysis has no user-selected feature: its completeness dimension
// is whether vital status (death) was looked for, so the availability headline
// is fixed rather than named after a feature.
const SURVIVAL_AVAILABILITY_HEADLINE = "Death info availability";

const Cohorts: React.FC<{
  chartLegendInfo: LegendProps[];
}> = ({ chartLegendInfo }) => {
  const insightsFilterSlice = useSelector(
    (state: RootState) => state.insightsFilter
  );

  const { cohorts, features } = insightsFilterSlice;

  const featureTypesState = useSelector(
    (state: RootState) => state.featureTypes
  );

  const { data } = featureTypesState;

  const unit = getUnitFromFeatureOrCohort(data, cohorts);

  // With no cohort selected, the legend stratifies by data availability (with
  // value / looked for, no value / not looked for) rather than by a cohort, so
  // name the headline after that completeness dimension - like the cohort case
  // names the cohort-defining feature. Signalled by the "With value" slice.
  // For descriptive statistics that dimension is the analyzed feature; for
  // survival analysis (scatter) it is fixed vital-status availability.
  const isAvailabilityLegend =
    cohorts.value === Insights.FULL_SAMPLE &&
    chartLegendInfo?.some(
      (item) => item.name === HAS_VALUE_LABEL
    );
  const isSurvivalLegend =
    chartLegendInfo?.[0]?.type === ChartType.SCATTER;

  let headline = cohorts.value;
  if (isAvailabilityLegend) {
    if (isSurvivalLegend) {
      headline = SURVIVAL_AVAILABILITY_HEADLINE;
    } else if (features?.value) {
      headline = `${features.value} availability`;
    }
  }

  const columns = [];

  for (let i = 0; i < chartLegendInfo?.length; i += 3) {
    columns.push(chartLegendInfo.slice(i, i + 3));
  }

  return (
    <IonGrid className={styles.cohorts}>
      <div className={styles.label}>
        {headline}{" "}
        {/**
         * The unit qualifies feature/cohort values; it doesn't apply to the
         * availability legend (whose items are completeness states, not
         * values), so it is omitted there.
         */}
        {!isAvailabilityLegend && unit ? (
          <span>({unit?.split("|")[0].trim() ?? ""})</span>
        ) : undefined}
      </div>
      <IonRow className={styles.row}>
        {columns.map(
          (column: LegendProps[], index: number) => (
            <IonCol key={index} size="4">
              {column.map(
                (item: LegendProps, index: number) => (
                  <div
                    className={styles.cohort}
                    key={index}
                  >
                    <div className={styles.flex_container}>
                      <ColorLabel color={item.color} />
                      <div className={styles.name}>
                        {item.name}
                      </div>
                    </div>
                  </div>
                )
              )}
            </IonCol>
          )
        )}
      </IonRow>
    </IonGrid>
  );
};

export default Cohorts;
